import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { getWorkflowRuns, getWorkflowSteps, triggerExcelExtraction, uploadAuditFiles, WorkflowRun, WorkflowRunStep } from '../src/Utils/workflowRunApi';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Download, ChevronDown, ChevronRight, Play, Upload, X, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { PaginationCustom } from './ui/pagination-custom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';

export const WorkflowRunLogsView: React.FC = () => {
  const { token } = useAuth();
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Changed: Support multiple expanded runs
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  // Changed: Cache steps for each run to avoid re-fetching
  const [stepsCache, setStepsCache] = useState<Record<string, WorkflowRunStep[]>>({});
  // Changed: Track loading state for each run individually
  const [loadingRuns, setLoadingRuns] = useState<Set<string>>(new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Upload State
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);


  const handleRunWorkflow = async () => {
    if (!token) return;

    setIsRunning(true);
    try {
      const response = await triggerExcelExtraction(token);

      if (response.success) {
        toast.success('Workflow started successfully', {
          description: 'The extraction process is running in the background.'
        });
        // Refresh runs after a short delay to see the new run
        setTimeout(() => {
          setStatusFilter('all'); // Reset filter to see new run
          setCurrentPage(1);      // Go to first page
          setRefreshTrigger(prev => prev + 1); // Force refresh
          setRuns(prev => []); // Clear runs to show loading/refresh visual
        }, 1000);
      } else {
        toast.error('Failed to start workflow', {
          description: response.message || 'Unknown error occurred'
        });
      }
    } catch (error: any) {
      console.error('Error starting workflow:', error);
      toast.error('Failed to start workflow', {
        description: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = async () => {
    if (!token || uploadFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }


    setIsUploading(true);
    try {
      const formData = new FormData();
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await uploadAuditFiles(formData, token);

      if (response.success) {
        toast.success('Files uploaded successfully');
        setShowUploadDialog(false);
        setUploadFiles([]);
        // Optional: Refresh runs if upload triggers a run or if we want to see logs
        setTimeout(() => {
          setRefreshTrigger(prev => prev + 1);
        }, 1000);
      } else {
        toast.error('Failed to upload files', {
          description: response.message
        });
      }
    } catch (error: any) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files', {
        description: error.message
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Fetch workflow runs
  useEffect(() => {
    const fetchRuns = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const filters: any = {
          page: currentPage,
          limit: itemsPerPage,
        };

        if (statusFilter !== 'all') {
          filters.status = statusFilter;
        }

        const response = await getWorkflowRuns(filters, token);

        if (response.success) {
          setRuns(response.data.workflowRuns);
          setTotalPages(response.data.pagination.totalPages);
          setTotalCount(response.data.pagination.totalCount);
        } else {
          toast.error('Failed to load workflow runs');
        }
      } catch (error: any) {
        console.error('Error fetching workflow runs:', error);
        toast.error(error.message || 'Failed to load workflow runs');
      } finally {
        setLoading(false);
      }
    };

    fetchRuns();
  }, [token, currentPage, itemsPerPage, statusFilter, refreshTrigger]);

  const toggleRunExpansion = async (runId: string) => {
    setExpandedRuns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(runId)) {
        newSet.delete(runId);
      } else {
        newSet.add(runId);
        // If we're expanding and we don't have the steps yet, fetch them
        if (!stepsCache[runId]) {
          fetchStepsForRun(runId);
        }
      }
      return newSet;
    });
  };

  const fetchStepsForRun = async (runId: string) => {
    if (!token) return;

    setLoadingRuns(prev => new Set(prev).add(runId));
    try {
      const response = await getWorkflowSteps(runId, token);

      if (response.success) {
        setStepsCache(prev => ({
          ...prev,
          [runId]: response.data.steps
        }));
      } else {
        toast.error('Failed to load workflow steps');
      }
    } catch (error: any) {
      console.error('Error fetching workflow steps:', error);
      toast.error(error.message || 'Failed to load workflow steps');
    } finally {
      setLoadingRuns(prev => {
        const newSet = new Set(prev);
        newSet.delete(runId);
        return newSet;
      });
    }
  };

  const getStatusBadge = (status: WorkflowRun['status']) => {
    const variants = {
      RUNNING: { variant: 'secondary' as const, className: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Running' },
      COMPLETED: { variant: 'default' as const, className: 'bg-green-100 text-green-800 border-green-300', label: 'Completed' },
      FAILED: { variant: 'destructive' as const, className: 'bg-red-100 text-red-800 border-red-300', label: 'Failed' },
      PARTIAL: { variant: 'outline' as const, className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Partial' },
    };

    const config = variants[status] || variants.RUNNING;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };


  const getStepTypeLabel = (stepType: WorkflowRunStep['stepType']) => {
    const labels: Record<string, string> = {
      EXTRACTION_START: 'Extraction Start',
      EXTRACTION_DONE: 'Extraction Done',
      AUDIT_ITEM: 'Audit Item',
      UPSERT_TASK: 'Upsert Task',
      SEND_EMAIL: 'Send Email',
      EMAIL_SKIPPED: 'Email Skipped',
      ERROR: 'Error',
    };
    return labels[stepType] || stepType;
  };

  const handleDownloadLogs = (run: WorkflowRun) => {
    const steps = stepsCache[run.runId] || [];
    const logData = {
      runId: run.runId,
      workflowName: run.workflowName,
      status: run.status,
      scheduledFor: run.scheduledFor,
      startedAt: run.startedAt,
      completedAt: run.completedAt,
      summary: run.summary,
      error: run.error,
      steps: steps,
    };

    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-run-${run.runId}-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Logs downloaded successfully');
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Workflow Run Logs</h1>
          <p className="text-sm text-gray-600 mt-1">
            View and analyze workflow execution logs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload Files
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Audit Files</DialogTitle>
                <DialogDescription>
                  Select files to upload to the audit platform. These will be processed by the workflow.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="files">Files</Label>
                  <Input
                    id="files"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="cursor-pointer"
                  />
                </div>
                {uploadFiles.length > 0 && (
                  <div className="border rounded-md p-2 space-y-2 max-h-[200px] overflow-y-auto">
                    {uploadFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-2 truncate">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <span className="truncate max-w-[200px]">{file.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
                          onClick={() => removeFile(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
                <Button
                  onClick={handleUploadFiles}
                  disabled={isUploading || uploadFiles.length === 0}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button
            onClick={handleRunWorkflow}
            disabled={isRunning}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Workflow
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="space-y-2 w-48">
              <Label htmlFor="status" className="text-xs">Status</Label>
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger id="status" className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="RUNNING">Running</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="PARTIAL">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workflow Runs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Workflow Runs ({totalCount})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-600">Loading workflow runs...</span>
            </div>
          ) : runs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600">No workflow runs found</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Run ID</TableHead>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Scheduled</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Status</TableHead>
                      {/* <TableHead>Steps</TableHead> */}
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run) => {
                      const isExpanded = expandedRuns.has(run.runId);
                      const steps = stepsCache[run.runId] || [];
                      const isLoadingSteps = loadingRuns.has(run.runId);
                      return (
                        <React.Fragment key={run.id}>
                          <TableRow
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => toggleRunExpansion(run.runId)}
                          >
                            <TableCell>
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">{run.runId}</TableCell>
                            <TableCell>{run.workflowName}</TableCell>
                            <TableCell>
                              {format(new Date(run.scheduledFor), 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              {format(new Date(run.startedAt), 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              {run.completedAt
                                ? format(new Date(run.completedAt), 'MMM dd, yyyy HH:mm')
                                : '-'}
                            </TableCell>
                            <TableCell>{getStatusBadge(run.status)}</TableCell>
                            {/* <TableCell className="text-center">
                              {run._count?.steps || 0}
                            </TableCell> */}
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadLogs(run);
                                }}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow className="animate-slide-down">
                              <TableCell colSpan={9} className="bg-gray-50 p-4">
                                {isLoadingSteps ? (
                                  <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                  </div>
                                ) : steps.length === 0 ? (
                                  <p className="text-sm text-gray-500 text-center py-4">No steps found</p>
                                ) : (
                                  <div className="space-y-2">
                                    <h4 className="font-semibold text-sm mb-3">Step-by-Step Timeline</h4>
                                    <div className="font-mono text-xs bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto max-h-[500px]">
                                      {steps.map((step) => {
                                        const getLogMessage = (step: WorkflowRunStep) => {
                                          const details = step.details || {};
                                          switch (step.stepType) {
                                            case 'EXTRACTION_START':
                                              return `Extraction Started: ${details.fileName || details.directoryPath || 'Unknown Source'}`;
                                            case 'EXTRACTION_DONE':
                                              return `Extraction Completed. Processed ${details.filesProcessed} files.${details.duration ? ` Duration: ${details.duration}ms` : ''}`;
                                            case 'UPSERT_TASK':
                                              return `Upsert Task: ${details.result} - ${details.itemCode} (${details.itemId})`;
                                            case 'AUDIT_ITEM':
                                              return `Audit Item: ${details.itemCode}`;
                                            case 'SEND_EMAIL':
                                              return `Email Sent to ${details.recipient}`;
                                            case 'EMAIL_SKIPPED':
                                              return `Email Skipped: ${details.reason}`;
                                            case 'ERROR':
                                              return `Error: ${details.error || 'Unknown Error'}`;
                                            default:
                                              return `${getStepTypeLabel(step.stepType)} - ${JSON.stringify(details)}`;
                                          }
                                        };

                                        return (
                                          <React.Fragment key={step.id}>
                                            {!(step.stepType === "EXTRACTION_START" && !step.details?.fileName) && (
                                              <div className="mb-1 whitespace-pre-wrap">
                                                <span className="text-gray-500 mr-2">
                                                  [{format(new Date(step.timestamp), 'yyyy-MM-dd HH:mm:ss.SSS')}]
                                                </span>
                                                <span className={
                                                  step.status === 'SUCCESS' ? 'text-green-400' :
                                                    step.status === 'FAILED' ? 'text-red-400' :
                                                      step.status === 'STARTED' ? 'text-blue-400' :
                                                        'text-yellow-400'
                                                }>
                                                  {step.status}
                                                </span>
                                                <span className="mx-2">:</span>
                                                <span>{getLogMessage(step)}</span>
                                              </div>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </div>
                                    {/* {run.summary && (
                                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                        <h5 className="font-semibold text-sm mb-2">Summary</h5>
                                        <div className="grid grid-cols-4 gap-4 text-sm">
                                          {run.summary.totalTasks !== undefined && (
                                            <div>
                                              <span className="text-gray-600">Total Tasks:</span>{' '}
                                              <span className="font-medium">{run.summary.totalTasks}</span>
                                            </div>
                                          )}
                                          {run.summary.created !== undefined && (
                                            <div>
                                              <span className="text-gray-600">Created:</span>{' '}
                                              <span className="font-medium text-green-600">{run.summary.created}</span>
                                            </div>
                                          )}
                                          {run.summary.updated !== undefined && (
                                            <div>
                                              <span className="text-gray-600">Updated:</span>{' '}
                                              <span className="font-medium text-blue-600">{run.summary.updated}</span>
                                            </div>
                                          )}
                                          {run.summary.failed !== undefined && (
                                            <div>
                                              <span className="text-gray-600">Failed:</span>{' '}
                                              <span className="font-medium text-red-600">{run.summary.failed}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )} */}
                                    {run.error && (
                                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                        <h5 className="font-semibold text-sm text-red-800 mb-2">Error</h5>
                                        <p className="text-sm text-red-700">{run.error.message}</p>
                                        {run.error.code && (
                                          <p className="text-xs text-red-600 mt-1">Code: {run.error.code}</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <PaginationCustom
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalCount}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={() => { }}
                    showPageSizeSelector={false}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

